import React from "react";
import { PersonData } from "@/entities/people/types";
import { useTranslation } from "react-i18next";
import BulkActionPopover, {
  BulkAction
} from "@/components/ui/BulkActionPopover";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { getEnumTKey } from "@/utils/strings";

const TRANSLATION_KEY = "detailedView.peopleTab.bulkActions";

interface PeopleBulkActionPopoverProps {
  selectedPeople: PersonData[];
  onRemoveFromDeal: () => void;
  onClose: () => void;
  manuallyAddedPeople?: string[];
  automaticallyAddedPeople?: string[];
  entityType?: EntityType;
}

const PeopleBulkActionPopover: React.FC<PeopleBulkActionPopoverProps> = ({
  selectedPeople,
  onRemoveFromDeal,
  onClose,
  manuallyAddedPeople,
  automaticallyAddedPeople,
  entityType = EntityType.DEAL
}) => {
  const { t } = useTranslation();
  const entityName = t(
    getEnumTKey("entities", "names", EntityType, entityType)
  ).toLowerCase();

  const selectedPeopleIds = selectedPeople.map((person) => person.id);
  // A person is only removable if they are in manuallyAddedPeople AND NOT in automaticallyAddedPeople
  const removablePeople =
    manuallyAddedPeople && manuallyAddedPeople.length > 0
      ? selectedPeopleIds.filter(
          (id) =>
            manuallyAddedPeople.includes(id) &&
            !automaticallyAddedPeople?.includes(id)
        )
      : [];

  const isRemoveDisabled = removablePeople.length === 0;
  const hasPartialSelection =
    removablePeople.length > 0 &&
    removablePeople.length < selectedPeopleIds.length;
  let tooltipText = "";

  if (isRemoveDisabled) {
    if (selectedPeople.length === 1) {
      tooltipText = t(
        `${TRANSLATION_KEY}.removeConfirmationModal.disabledTooltip.single`,
        { entityName }
      );
    } else {
      tooltipText = t(
        `${TRANSLATION_KEY}.removeConfirmationModal.disabledTooltip.all`,
        { entityName }
      );
    }
  } else if (hasPartialSelection) {
    tooltipText = t(
      `${TRANSLATION_KEY}.removeConfirmationModal.disabledTooltip.some`,
      { entityName }
    );
  }

  const actions: BulkAction[] = [
    {
      key: "removeFromDeal",
      label: t(`${TRANSLATION_KEY}.removeFromDeal`, { entityName }),
      onClick: onRemoveFromDeal,
      disabled: isRemoveDisabled,
      tooltipText: tooltipText
    }
  ];

  return (
    <BulkActionPopover
      selectedCount={selectedPeople.length}
      selectedText={t("detailedView.peopleTab.bulkActions.selected")}
      actions={actions}
      onClose={onClose}
    />
  );
};

export default PeopleBulkActionPopover;
